import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { routerSyncStage, routerSyncText, type HexSyncState, type RouterSyncStage } from '@/utils/hexPortalProgress';

/**
 * Banner under a MikroTik Hybrid's Save: saved → sending to the router → live
 * on the router. Prices, the portal look, the name and printed codes all
 * travel the same way, so one line covers every tab (Ray, 2026-10-21).
 * Same shape as PortalThemeProgress; the record's own instants rule once the
 * poll brings them.
 */
const RouterSyncProgress: React.FC<{
   sync: HexSyncState | null | undefined;
   savedAt?: number | null;
   onPoll?: () => void;
}> = ({ sync, savedAt, onPoll }) => {
   const [now, setNow] = useState(() => Date.now());
   useEffect(() => {
      setNow(Date.now());
   }, [savedAt, sync?.dirtyAt, sync?.liveAt]);

   const stage: RouterSyncStage = routerSyncStage(sync, savedAt ?? null, now);
   const active = stage === 'router-wait';
   const anchor = Math.max(savedAt ?? 0, sync?.dirtyAt ? new Date(sync.dirtyAt).getTime() : 0);

   useEffect(() => {
      if (!active) return;
      const tick = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(tick);
   }, [active]);
   const pollRef = useRef(onPoll);
   pollRef.current = onPoll;
   useEffect(() => {
      if (!active) return;
      // A direct push lands in seconds: poll fast while waiting.
      const poll = setInterval(() => pollRef.current?.(), 3_000);
      return () => clearInterval(poll);
   }, [active]);

   if (!stage) return null;
   return (
      <Banner data-testid="router-sync-progress" data-stage={stage} $stage={stage}>
         {active && <Dot />}
         {routerSyncText(stage, Math.max(0, now - anchor), sync?.error)}
      </Banner>
   );
};

export default RouterSyncProgress;

const Banner = styled.div<{ $stage: RouterSyncStage }>`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-top: 10px;
   padding: 9px 12px;
   border-radius: 8px;
   font-size: 12.5px;
   font-weight: 600;
   background: ${({ $stage }) =>
      $stage === 'done' ? `${colors.SUCCESS}18` : $stage === 'stuck' ? `${colors.DANGER}14` : `${colors.WARNING}1c`};
   color: ${({ $stage }) =>
      $stage === 'done' ? colors.SUCCESS : $stage === 'stuck' ? colors.DANGER : colors.WARNING};
   border: 1px solid currentColor;
`;
const Dot = styled.span`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background: currentColor;
   animation: sf-router-blink 1s ease-in-out infinite;
   @keyframes sf-router-blink {
      50% {
         opacity: 0.25;
      }
   }
`;
